import RunButton from '@ui/button/RunButton';
import React, { useEffect, useRef } from 'react';
import CalendarMonth from '@app/state/calendar/CalendarMonth';
import ProcessRequestExecutionState from '@app/model/process-request/ProcessRequestExecutionState';
import { isStateFinishing } from '@app/model/process-request/ProcessRequestState';
import Process from '@app/model/process-request/Process';
import { useQueryClient } from '@tanstack/react-query';
import ProcessRequestResult from '@app/model/process-request/ProcessRequestResult';
import { MarketStateGraphQLQueryKeys } from '@app/model/market_state/service/MarketStateGraphQLQueryKeys';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import MarketStateFilterInput from '@app/model/market_state/input/MarketStateFilterInput';
import {
	useMarketStateTools,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import {
	useLatestProcessRequestExecutionStateMonitor,
} from '@app/site/secured/rateshopper/rates/provider/useLatestProcessRequestExecutionStateMonitor';

export const RefreshPriceRecommendationButton: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	calendarMonth: CalendarMonth,
	filter: MarketStateFilterInput,
	label?: string,
	hint?: string,
	state?: 'primary' | 'white',
	size?: 'normal' | 'small',
	fullWidth?: boolean,
}> = ({ companyHotel, calendarMonth, filter, label = 'Update Recommendations', hint, state = 'white', size = 'normal', fullWidth = false }) => {

	const toaster = useToaster();
	const tools = useMarketStateTools();
	const monitor = useLatestProcessRequestExecutionStateMonitor();
	const handledRequestId = useRef<number | null>(null);
	const restoredActiveRequestId = useRef<number | null>(null);
	const output = isPriceRecommendationProcess(monitor.latestActiveExecutionState, companyHotel.id)
		? monitor.latestActiveExecutionState
		: undefined;

	const triggerUpdate = () => {
		tools.triggerPriceRecommendationUpdate()
			.then((state) => {
				handledRequestId.current = null;
				restoredActiveRequestId.current = null;
				monitor.setLatestActiveExecutionState(new ProcessRequestExecutionState(state));
			})
			.catch(() => {
				toaster.error('Dashboard', 'Calendar could not be refreshed at the moment. Please try again later!');
			});
	};
	const queryClient = useQueryClient();

	useEffect(() => {
		if (!output) return;

		if (!output.isFinished) {
			if (restoredActiveRequestId.current !== output.requestId) {
				restoredActiveRequestId.current = output.requestId;
				tools.toggleMarketStateIsUpdating(true);
			}
			return;
		}

		if (handledRequestId.current === output.requestId) return;

		handledRequestId.current = output.requestId;

		if (output.result === ProcessRequestResult.SUCCESS)
			toaster.success('Dashboard', 'Calendar refreshed successfully!');
		if (output.result === ProcessRequestResult.FAILED)
			toaster.error('Dashboard', 'Calendar could not be refreshed at the moment. Please try again later!');

		queryClient.refetchQueries({
			queryKey: MarketStateGraphQLQueryKeys.details(
				companyHotel.id,
				calendarMonth.index.year,
				calendarMonth.index.month,
				filter),
			type: 'active',
		}).then(() => tools.toggleMarketStateIsUpdating(false));
		restoredActiveRequestId.current = null;
		monitor.setLatestActiveExecutionState(null);
	}, [calendarMonth.index.month, calendarMonth.index.year, companyHotel.id, filter, monitor, output, queryClient, toaster, tools]);

	const outputStats = output?.progressPercentage ?? 0;
	const isUpdatingFinished = !!output && !output.isFinished && isStateFinishing(output.state) && outputStats >= 100;
	const isUpdating = !!output && !output.isFinished && !isUpdatingFinished;

	const phase = isUpdatingFinished ? 'finishing' : isUpdating ? 'running' : 'idle';
	return <RunButton label={phase === 'idle' ? label : 'Updating'}
		hint={phase === 'running' ? `${Math.round(outputStats)}%` : phase === 'idle' ? hint : undefined}
		state={state}
		size={size}
		fullWidth={fullWidth}
		phase={phase}
		progress={outputStats}
		disabled={monitor.isInteractionLocked || tools.calendarMonth.isInPast()}
		onClick={triggerUpdate} />;
};

const isPriceRecommendationProcess = (output: ProcessRequestExecutionState | null, companyHotelId: number): output is ProcessRequestExecutionState => {
	if (!output) return false;
	if (output.companyHotelId !== companyHotelId) return false;

	return output.process === Process.PRICE_RECOMMENDATION;
};
